import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, Tab, Tabs } from "react-bootstrap";
import { showToast } from "../../../shared/helpers";
import trashIcon from "../../../../assets/icons/delete.svg";
import uploadIcon from "../../../../assets/icons/upload.svg";

const signatureFonts = ["Pacifico", "Alex Brush", "Nothing You Could Do"];

const renderSignatureDataUrl = (name, fontFamily, options = {}) => {
  if (!name || name.trim() === "") return "";
  const {
    width: requestedWidth = 450,
    height = 55,
    fontSize = 34,
    shadowBlur = 1.5,
    leftPadding = 0,
  } = options;
  const dpr = (typeof window !== "undefined" && window.devicePixelRatio) || 1;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";
  ctx.font = `${fontSize}px '${fontFamily}'`;
  const metrics = ctx.measureText(name);
  const textWidth = Math.ceil(
    (metrics.actualBoundingBoxLeft || 0) +
      (metrics.actualBoundingBoxRight || metrics.width || 0)
  );
  const width = Math.min(
    requestedWidth,
    Math.max(textWidth + leftPadding, leftPadding + 1)
  );
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = "#000";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.shadowColor = "rgba(0, 0, 0, 0.2)";
  ctx.shadowBlur = shadowBlur;
  ctx.font = `${fontSize}px '${fontFamily}'`;
  ctx.fillText(name, leftPadding, height / 2);
  return canvas.toDataURL("image/png");
};

const renderSignatureByIndex = (name, index, options = {}) => {
  const font = signatureFonts[index];
  if (!font) return "";
  return renderSignatureDataUrl(name, font, options);
};

const SignatureModal = ({
  show,
  onHide,
  onSave,
  selectedSignatureName,
  selectedSignatureImage,
  selectedSignatureIdx,
}) => {
  const [activeTab, setActiveTab] = useState("type");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [signatures, setSignatures] = useState([]);
  const [selectedSignature, setSelectedSignature] = useState("");
  const [activeSignatureIdx, setActiveSignatureIdx] = useState(null);
  const [uploadedSignature, setUploadedSignature] = useState("");
  const [fontsReady, setFontsReady] = useState(false);

  useEffect(() => {
    if (!show) return;
    const hasImageSignature =
      !!selectedSignatureImage &&
      (selectedSignatureIdx === null || selectedSignatureIdx === undefined);
    if (hasImageSignature) {
      setFirstName("");
      setLastName("");
      setSignatures([]);
      setSelectedSignature(selectedSignatureImage);
      setActiveSignatureIdx(null);
    } else {
      const parts = (selectedSignatureName || "").trim().split(/\s+/);
      setFirstName(parts[0] || "");
      setLastName(parts.slice(1).join(" "));
      setSelectedSignature(selectedSignatureImage || "");
      setActiveSignatureIdx(
        selectedSignatureIdx === 0 ? 0 : selectedSignatureIdx || null
      );
    }
    setUploadedSignature(hasImageSignature ? selectedSignatureImage : "");
    setActiveTab(hasImageSignature ? "image" : "type");
    setFontsReady(false);
  }, [
    show,
    selectedSignatureName,
    selectedSignatureImage,
    selectedSignatureIdx,
  ]);

  const fullName = useMemo(() => {
    return [firstName, lastName].filter(Boolean).join(" ").trim();
  }, [firstName, lastName]);

  useEffect(() => {
    if (!show) return;
    let isCancelled = false;
    const loadFonts = async () => {
      if (!document?.fonts?.load) {
        setFontsReady(true);
        return;
      }
      try {
        const sampleText = fullName || "Signature";
        await Promise.all(
          signatureFonts.map((font) =>
            document.fonts.load(`36px "${font}"`, sampleText)
          )
        );
        await document.fonts.ready;
      } catch (error) {
        // Proceed with default fonts if loading fails.
      }
      if (!isCancelled) setFontsReady(true);
    };

    setFontsReady(false);
    loadFonts();
    return () => {
      isCancelled = true;
    };
  }, [show, fullName]);

  useEffect(() => {
    if (!fullName || fullName.trim() === "") {
      setSignatures([]);
      return;
    }
    if (!fontsReady) return;
    const newSignatures = signatureFonts.map((_, idx) =>
      renderSignatureByIndex(fullName, idx)
    );
    setSignatures(newSignatures);
  }, [fullName, fontsReady]);

  const handleSelectedFile = (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      showToast("Max file size is 10MB.");
      return;
    }
    if (!file.type.startsWith("image/")) {
      showToast("Only image files can be used for signatures.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        setFirstName("");
        setLastName("");
        setSignatures([]);
        setUploadedSignature(reader.result);
        setSelectedSignature(reader.result);
        setActiveSignatureIdx(null);
        setActiveTab("image");
      }
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    handleSelectedFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    handleSelectedFile(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleSave = () => {
    if (!selectedSignature) return;
    onSave({
      signature: activeTab === "image" ? "" : fullName,
      signatureImage: selectedSignature,
      signatureIdx: activeSignatureIdx,
      signatureType: activeTab === "image" ? "image" : "typed",
    });
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      className="signature-modal"
    >
      <Modal.Header closeButton className="no-border pb-0">
        <Modal.Title>Create your signature</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tabs
          activeKey={activeTab}
          onSelect={(key) => setActiveTab(key || "type")}
          className="signature-tabs mb-3"
        >
          <Tab eventKey="type" title="Type">
            <div className="signature-type-form">
              <div className="signature-name-row">
                <input
                  className="form-control"
                  placeholder="First Name"
                  value={firstName}
                  onChange={(event) => {
                    setFirstName(event.target.value);
                    setSelectedSignature("");
                    setActiveSignatureIdx(null);
                  }}
                />
                <input
                  className="form-control"
                  placeholder="Last Name"
                  value={lastName}
                  onChange={(event) => {
                    setLastName(event.target.value);
                    setSelectedSignature("");
                    setActiveSignatureIdx(null);
                  }}
                />
              </div>
              <div className="signature-options">
                {signatures.map((sig, idx) => (
                  <button
                    type="button"
                    key={`${sig}-${idx}`}
                    className={`signature-option ${
                      activeSignatureIdx === idx ? "active" : ""
                    }`}
                    onClick={() => {
                      setSelectedSignature(sig);
                      setActiveSignatureIdx(idx);
                    }}
                    aria-pressed={activeSignatureIdx === idx}
                  >
                    <input
                      type="radio"
                      name="signature-option"
                      checked={activeSignatureIdx === idx}
                      readOnly
                    />
                    <img src={sig} alt={`Signature ${idx + 1}`} />
                  </button>
                ))}
              </div>
              <div className="signature-type-indicator">
                By creating this signature, I consent to its use, along with my
                initials, as my electronic signature for any purpose, including
                legally binding documents.
              </div>
            </div>
          </Tab>
          <Tab eventKey="image" title="Image">
            <div className="signature-image-upload">
              <label
                className="signature-upload-box"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                <input
                  type="file"
                  accept="image/*,.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleFileChange}
                />
                <img src={uploadIcon} alt="Upload" />
                <span>Drag & Drop or Click to Upload</span>
                <span className="signature-upload-hint">
                  PDF, JPG, PNG, DOC (Max size: 10MB)
                </span>
              </label>
              <div className="signature-type-indicator">
                By creating this signature, I consent to its use, along with my
                initials, as my electronic signature for any purpose, including
                legally binding documents.
              </div>
              {uploadedSignature && (
                <div className="signature-image-preview">
                  <img src={uploadedSignature} alt="Uploaded signature" />
                  <button
                    type="button"
                    className="signature-remove-btn"
                    onClick={() => {
                      setUploadedSignature("");
                      setSelectedSignature("");
                    }}
                  >
                    <img src={trashIcon} alt="Remove" />
                  </button>
                </div>
              )}
            </div>
          </Tab>
        </Tabs>

        <div className="signature-modal-actions">
          <Button variant="outline-primary" onClick={onHide}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!selectedSignature || (activeTab === "type" && !fullName)}
          >
            Create
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default SignatureModal;
